// CMSP host integration. The widget and the headless SDK package know nothing
// about these endpoints or the host's authentication mechanism.
const loadClient = () => import('../../omnichannel/dist/omnichannel.js');
const coordinators = new Set();
const resumeHints = new Map();
const resumePrefix = 'cmsp.omnichannel.resume.';

/** Ownership teardown, not view cleanup. SDK disposal closes the provider chat. */
export async function disposeOmnichannelCoordinators() {
  const results = await Promise.allSettled([...coordinators].map(coordinator => coordinator.dispose()));
  const failure = results.find(result => result.status === 'rejected');
  if (failure) throw failure.reason;
}

function aborted() {
  return new DOMException('The operation was aborted.', 'AbortError');
}

function scanFailure(error) {
  const status = Number(error?.status);
  const code = status === 400 && error?.code === 'live_chat_message_too_long'
    ? 'live_chat_message_too_long' : 'live_chat_scan_unavailable';
  return Object.assign(new Error(code), {
    code, operation: 'scan',
    retryable: error?.name !== 'AbortError' && !(status >= 400 && status < 500 && ![408, 425, 429].includes(status)),
    ...(Number.isInteger(status) && status >= 100 && status <= 599 ? { status } : {}),
  });
}

function sourceId(message) {
  const id = message.providerMessageId || message.eventId;
  return id ? `sdk:${message.chatSessionId}:${id}` : undefined;
}

function validateOperationId(value) {
  if (value !== undefined && (typeof value !== 'string' || !/^[a-zA-Z0-9_-]{1,128}$/.test(value))) {
    throw Object.assign(new Error('invalid_client_operation_id'), { code: 'invalid_client_operation_id' });
  }
}

function historyRow(message) {
  const names = (message.attachments ?? []).map(file => file.name).filter(Boolean).join(', ');
  return {
    role: message.isHumanAgent || message.actor === 'bot' ? 'agent' : 'user',
    text: [message.text, names ? `📎 ${names}` : ''].filter(Boolean).join('\n\n'),
    messageId: sourceId(message),
    ...(message.clientOperationId ? { clientOperationId: message.clientOperationId } : {}),
    senderName: message.senderName,
    isLiveAgent: message.isHumanAgent === true,
    ...(message.readSequence ? { readSequence: message.readSequence } : {}),
    ...(message.readOn ? { readOn: message.readOn } : {}),
    ...(message.actor === 'bot' ? { senderKind: 'bot' } : {}),
    at: message.occurredAt,
    ...(message.attachments?.length ? {
      // Keep the text-only display contract while rich consumers use the unmodified body.
      bodyText: message.text ?? '',
      attachments: message.attachments.map(file => ({
        fileId: file.id, fileName: file.name, contentType: file.contentType,
      })),
    } : {}),
  };
}

function lifecycleCategory(type) {
  return {
    session_started: 'waiting', session_restored: 'waiting',
    connecting: 'connecting', queue_position: 'queue-position',
    agent_joined: 'agent-joined', agent_left: 'agent-left', transfer: 'transferred',
    agent_ended: 'ended', conversation_ended: 'ended',
  }[type] ?? 'system';
}

export function mergeOmnichannelHistory(history = [], messages = []) {
  const rows = [...history];
  const seen = new Set(rows.map(row => row.messageId).filter(Boolean));
  for (const message of messages) {
    const row = historyRow(message);
    if (row.messageId && seen.has(row.messageId)) continue;
    if (row.messageId) seen.add(row.messageId);
    rows.push(row);
  }
  return rows;
}

export function liveFrameFor(event) {
  const identity = {
    messageId: event.messageId, senderName: event.senderName,
    ...(event.clientOperationId ? { clientOperationId: event.clientOperationId } : {}),
    ...(event.messageUpdated ? { messageUpdated: true } : {}),
    ...(event.readSequence !== undefined ? { readSequence: event.readSequence } : {}),
    ...(event.readOn ? { readOn: event.readOn } : {}),
    ...(event.liveCapabilities ? { liveCapabilities: event.liveCapabilities } : {}),
    ...(event.recoveryState ? { recoveryState: event.recoveryState } : {}),
    ...(event.operation ? { operation: event.operation, retryable: event.retryable } : {}),
    ...(event.status !== undefined ? { status: event.status } : {}),
    ...(event.liveCategory === 'message-scan-failed' ? { error: event.error } : {}),
  };
  if (event.type === 'error') return { ...identity, kind: 'system', text: '', category: 'error', error: event.error };
  if (event.mode === 'prechat-survey') {
    return { ...identity, kind: 'prechat_survey', text: '', survey: event.preChatSurvey };
  }
  if (event.mode === 'ended') {
    return { ...identity, kind: 'ended', text: event.text ?? '', category: 'ended', surveyUrl: event.postChatSurveyUrl };
  }
  const names = (event.attachments ?? []).map(file => file.fileName || 'file').join(', ');
  return {
    ...identity,
    kind: event.senderKind === 'human' ? 'agent' : event.senderKind === 'bot' ? 'bot' : 'system',
    text: [event.text, names ? `📎 ${names}` : ''].filter(Boolean).join('\n\n'),
    category: event.liveCategory,
  };
}

export function createOmnichannelCoordinator(options) {
  const sessions = new Map();
  const path = id => `${options.apiBase ?? '/api/v1/chat'}/${encodeURIComponent(id)}/omnichannel`;

  function stateFor(id) {
    let state = sessions.get(id);
    if (!state) {
      state = {
        enabled: false, bound: false, disposed: false, retiring: false, ended: false,
        lifetime: new AbortController(), pending: [], wake: null, watching: false,
        client: null, ready: null, sessionId: null, bindingId: null, survey: null,
        files: new Map(), started: null, seen: new Set(), messages: new Map(), acknowledgedOperations: new Set(),
        ending: false, terminal: null, postChatSurveyUrl: undefined, lastHeaders: null, teardownHeaders: null,
        closing: null, subscription: null, completed: false, unsent: [],
        history: [], resumeKey: null, resumeHint: null, reconnectId: null,
        read: new Set(), reading: new Map(),
        attempt: 0, recovery: 'idle', retryAbort: new AbortController(), endRequest: null,
        bootstrapRequest: null, bootstrapUncertain: false, controlEndRequest: null,
        failure: null, transportFailure: null, serverOwned: false, humanConnected: false, presentation: null,
        capabilities: { incomingReadReceipts: false, sendReadReceipts: true },
        requiresMessageScan: false,
      };
      sessions.set(id, state);
    }
    return state;
  }

  async function request(id, state, operation, body, tracked, signal) {
    const canClose = operation === 'end';
    const assertUsable = () => {
      if (signal?.aborted || state.lifetime.signal.aborted || state.disposed || (state.retiring && !canClose)
        || (operation === 'scan' && (state.ending || state.ended))) throw aborted();
      if (state.transportFailure && !canClose) throw state.transportFailure;
    };
    const checkOwner = async () => {
      if (state.retiring || !options.getIdentity) return;
      const owner = await options.getIdentity(id);
      assertUsable();
      if (!state.retiring && owner !== state.owner) {
        void api.dispose(id).catch(() => {});
        throw aborted();
      }
    };
    assertUsable();
    await checkOwner();
    let candidate;
    if (!state.retiring) {
      try {
        const resolved = await options.getHeaders(id);
        assertUsable();
        if (!state.retiring && resolved) candidate = { ...resolved };
      } catch (error) {
        assertUsable();
        if (!state.retiring) throw error;
      }
      await checkOwner();
    }
    assertUsable();
    // No awaits after this selection: teardown can only use its frozen old-owner snapshot,
    // even if a previously started credential/identity lookup resolved after retirement.
    const headers = state.retiring ? state.teardownHeaders : candidate;
    if (!headers) throw aborted();
    if (!state.retiring) state.lastHeaders = Object.freeze({ ...headers });
    if (tracked) {
      tracked.dispatched = true;
      tracked.headers = Object.freeze({ ...headers });
    }
    const response = await fetch(`${path(id)}/${operation}`, {
      method: body === undefined ? 'GET' : 'POST',
      headers: { ...headers, 'Content-Type': 'application/json' },
      credentials: 'same-origin',
      ...(body === undefined ? {} : { body: JSON.stringify(body) }),
      signal: signal ? AbortSignal.any([state.lifetime.signal, signal]) : state.lifetime.signal,
    });
    if (tracked) tracked.responseStatus = response.status;
    if (!response.ok) {
      if (operation === 'scan') {
        const problem = response.status === 400 ? await response.json().catch(() => undefined) : undefined;
        throw scanFailure({
          status: response.status,
          code: problem?.error === 'live_chat_message_too_long' ? 'live_chat_message_too_long' : undefined,
        });
      }
      throw Object.assign(new Error(`omnichannel_${operation}_${response.status}`), {
        status: response.status,
        code: response.status === 503 ? 'live_agent_unavailable' : `omnichannel_${operation}_failed`,
      });
    }
    const result = response.status === 204 ? undefined : await response.json();
    if (operation === 'scan') {
      await checkOwner();
      assertUsable();
    }
    return result;
  }

  function bootstrapControl(id, state, generation = state.attempt) {
    if (state.bootstrapRequest?.status === 'pending') return state.bootstrapRequest.promise;
    const tracked = { status: 'pending', dispatched: false, responseStatus: undefined, headers: null, promise: null };
    state.bootstrapRequest = tracked;
    tracked.promise = (async () => {
      try {
        const bootstrap = await request(id, state, 'bootstrap', { context: await contextFor(id) }, tracked);
        if (generation !== state.attempt || state.disposed) throw new Error('bootstrap_generation_mismatch');
        if (typeof bootstrap?.chatSessionId !== 'string' || !bootstrap.chatSessionId.trim() || bootstrap.chatSessionId.length > 128) {
          throw new Error('invalid_bootstrap_response');
        }
        if (state.sessionId && state.sessionId !== bootstrap.chatSessionId) {
          throw Object.assign(new Error('live_session_identity_mismatch'), { status: 409 });
        }
        // Reconcile the raw response even if the SDK's bounded initialize has rejected.
        // Retirement keeps this owner/generation and its original credentials until cleanup.
        state.sessionId = bootstrap.chatSessionId;
        state.reconnectId = bootstrap.reconnectId ?? null;
        const hint = restoreHint(id, state, bootstrap.chatSessionId);
        tracked.status = 'fulfilled';
        state.bootstrapUncertain = false;
        return { ...bootstrap, omnichannelConversationId: hint.omnichannelConversationId, restoreRequired: hint.restoreRequired };
      } catch (error) {
        const denied = [400, 401, 403, 404, 409, 422].includes(tracked.responseStatus);
        tracked.status = !tracked.dispatched || denied ? 'rejected' : 'unknown';
        if (tracked.status === 'unknown') state.bootstrapUncertain = true;
        throw error;
      }
    })();
    // The SDK may stop awaiting this request after its own timeout.
    tracked.promise.catch(() => {});
    return tracked.promise;
  }

  async function settleBootstrap(state) {
    const tracked = state.bootstrapRequest;
    if (tracked?.status === 'pending') {
      let timer;
      try {
        await Promise.race([
          tracked.promise.catch(() => undefined),
          new Promise((_, reject) => {
            timer = setTimeout(() => reject(Object.assign(new Error('bootstrap_unresolved'), {
              code: 'bootstrap_unresolved', operation: 'bootstrap_cleanup', retryable: true,
            })), 30000);
          }),
        ]);
      } finally { clearTimeout(timer); }
    }
    if (state.bootstrapUncertain) throw Object.assign(new Error('bootstrap_outcome_unknown'), {
      code: 'bootstrap_outcome_unknown', operation: 'bootstrap_cleanup', retryable: false,
    });
  }

  function completeControl(id, state, chatSessionId) {
    if (!chatSessionId || chatSessionId !== state.sessionId) return Promise.reject(new Error('live_session_identity_mismatch'));
    if (state.completed) return Promise.resolve();
    state.controlEndRequest ??= request(id, state, 'end', { chatSessionId }).then(() => {
      clearResume(state);
      state.completed = true;
    }).finally(() => {
      if (!state.completed) state.controlEndRequest = null;
    });
    return state.controlEndRequest;
  }

  async function contextFor(id) {
    const context = await options.getContext?.(id) ?? {};
    const allowed = {
      pageUrl: context.pageUrl,
      pageTitle: context.pageTitle,
      deviceType: context.deviceType ?? context.devicePlatform,
      browserLanguage: context.browserLanguage ?? context.language ?? context.locale,
      clientVersion: context.clientVersion,
    };
    return Object.fromEntries(Object.entries(allowed).filter(([, value]) =>
      typeof value === 'string' && value.length > 0 && value.length <= 2048));
  }

  function clearResume(state) {
    if (state.resumeKey) {
      resumeHints.delete(state.resumeKey);
      try { localStorage.removeItem(state.resumeKey); } catch { /* Memory-only recovery. */ }
    }
    state.resumeHint = null;
  }

  function restoreHint(id, state, chatSessionId) {
    state.resumeKey = typeof state.owner === 'string' && state.owner
      ? `${resumePrefix}${encodeURIComponent(JSON.stringify([options.apiBase ?? '/api/v1/chat', state.owner, id]))}`
      : null;
    let hint = state.resumeHint;
    if (state.resumeKey) {
      hint = resumeHints.get(state.resumeKey);
      if (!hint) {
        try { hint = JSON.parse(localStorage.getItem(state.resumeKey) ?? 'null'); } catch { /* Memory-only recovery. */ }
      }
    }
    if (hint?.chatSessionId !== chatSessionId || typeof hint?.omnichannelConversationId !== 'string'
      || !hint.omnichannelConversationId || hint.omnichannelConversationId.length > 512) {
      clearResume(state);
      return {};
    }
    state.resumeHint = { chatSessionId, omnichannelConversationId: hint.omnichannelConversationId };
    if (state.resumeKey) resumeHints.set(state.resumeKey, state.resumeHint);
    return { omnichannelConversationId: hint.omnichannelConversationId, restoreRequired: true };
  }

  function rememberSession(state, ids) {
    if (state.disposed || state.retiring) throw aborted();
    if (ids.chatSessionId !== state.sessionId || typeof ids.omnichannelConversationId !== 'string'
      || !ids.omnichannelConversationId || ids.omnichannelConversationId.length > 512) {
      throw new Error('live_session_identity_mismatch');
    }
    state.resumeHint = { chatSessionId: ids.chatSessionId, omnichannelConversationId: ids.omnichannelConversationId };
    if (state.resumeKey) {
      resumeHints.set(state.resumeKey, state.resumeHint);
      try { localStorage.setItem(state.resumeKey, JSON.stringify(state.resumeHint)); } catch { /* Memory-only recovery. */ }
    }
  }

  function publish(state, event) {
    if (state.disposed || state.retiring) return;
    state.pending.push(event);
    if (state.pending.length > 1000) state.pending.shift();
    state.wake?.();
  }

  function presentationPhase(event) {
    if (event.recoveryState === 'failed' || event.recoveryState === 'auth-failed'
      || event.liveCategory === 'error') return 'failed';
    if (event.liveCategory === 'connecting' || event.recoveryState === 'recovering') return 'connecting';
    if (event.liveCategory === 'waiting' || ['agent-left', 'transferred'].includes(event.liveCategory)) return 'waiting';
    if (event.senderKind === 'human' || event.liveCategory === 'agent-joined') return 'connected';
    return undefined;
  }

  function rememberPresentation(state, event) {
    const phase = presentationPhase(event);
    if (!phase) return;
    state.presentation = {
      phase,
      senderName: phase === 'connected' ? event.senderName ?? state.presentation?.senderName : undefined,
    };
  }

  function publishPresentation(state, event) {
    rememberPresentation(state, event);
    publish(state, event);
  }

  function presentationFrame(state) {
    const senderName = state.presentation?.senderName;
    switch (state.presentation?.phase) {
      case 'connecting':
        return {
          type: 'token', mode: 'live', senderKind: 'system', liveCategory: 'connecting',
          recoveryState: 'recovering', text: 'Connecting to live support…',
        };
      case 'waiting':
        return {
          type: 'token', mode: 'live', senderKind: 'system', liveCategory: 'waiting',
          recoveryState: 'active', text: 'Waiting for a support agent…',
        };
      case 'connected':
        return {
          type: 'token', mode: 'live', senderKind: 'system', liveCategory: 'agent-joined',
          recoveryState: 'active', ...(senderName ? { senderName } : {}), text: '',
        };
      case 'failed':
        return {
          type: 'error', mode: 'live', senderKind: 'system', liveCategory: 'error',
          recoveryState: 'failed', error: 'live_agent_unavailable',
        };
      default:
        return undefined;
    }
  }

  function acknowledge(state, confirmed, clientOperationId = confirmed?.clientOperationId) {
    if (!confirmed?.providerMessageId || confirmed.isHumanAgent || confirmed.actor === 'bot'
      || (clientOperationId && confirmed.clientOperationId !== clientOperationId)) return undefined;
    const messageId = confirmed?.providerMessageId ? sourceId({ ...confirmed, chatSessionId: state.sessionId }) : undefined;
    const acknowledgement = {
      ...(clientOperationId ? { clientOperationId } : {}),
      ...(messageId ? { messageId, readSequence: confirmed.readSequence,
        readOn: state.messages.get(messageId)?.readOn } : {}),
    };
    const key = clientOperationId ?? messageId;
    if (!key || !state.acknowledgedOperations.has(key)) {
      if (key && messageId) state.acknowledgedOperations.add(key);
      publish(state, { type: 'token', mode: 'live', senderKind: 'system', liveCategory: 'message-sent', ...acknowledgement });
    }
    return acknowledgement;
  }

  function normalizedEvent(id, state, event) {
    if (state.transportFailure && !state.ending) return;
    if (event.type === 'capabilities') {
      state.capabilities = { incomingReadReceipts: event.incomingReadReceipts, sendReadReceipts: event.sendReadReceipts };
      publish(state, { type: 'token', mode: 'live', senderKind: 'system', liveCategory: 'capabilities',
        liveCapabilities: state.capabilities });
      return;
    }
    if (event.type === 'read-receipt') {
      const messageId = sourceId({ ...event, chatSessionId: state.sessionId });
      const message = state.messages.get(messageId);
      if (message?.actor !== 'customer' || message.readSequence !== event.readSequence) return;
      for (const row of state.messages.values()) {
        if (row.actor === 'customer' && row.readSequence !== undefined && row.readSequence <= event.readSequence) {
          row.readOn ??= event.readOn;
        }
      }
      publish(state, { type: 'token', mode: 'live', senderKind: 'system', liveCategory: 'read-receipt',
        messageId, readSequence: event.readSequence, readOn: event.readOn });
      return;
    }
    if (state.disposed || state.retiring) return;
    const data = event.message ?? event.event ?? event;
    const identity = {
      conversationId: id, sessionId: state.bindingId,
      messageId: sourceId({ ...data, chatSessionId: state.sessionId }), senderName: data.senderName,
      ...(data.clientOperationId ? { clientOperationId: data.clientOperationId } : {}),
    };
    const isMessage = event.type === 'message' || event.type === 'message-updated';
    const previous = isMessage ? state.messages.get(identity.messageId) : undefined;
    const correlated = isMessage && data.actor === 'customer' && data.clientOperationId
      && previous?.clientOperationId !== data.clientOperationId;
    const changed = previous && JSON.stringify([previous.text, previous.senderName, previous.attachments ?? []])
      !== JSON.stringify([data.text, data.senderName, data.attachments ?? []]);
    const messageUpdated = !!changed && state.seen.has(identity.messageId);
    if (isMessage && identity.messageId) {
      state.messages.set(identity.messageId, { ...data, chatSessionId: state.sessionId });
    }
    if (identity.messageId) {
      if (state.seen.has(identity.messageId) && !messageUpdated && !correlated) return;
      state.seen.add(identity.messageId);
    }
    if (isMessage) {
      if (correlated) acknowledge(state, data);
      if (!data.isHumanAgent && data.actor !== 'bot' && !messageUpdated) return; // The composer owns the customer's send acknowledgement.
      const attachments = data.attachments?.map(file => {
        state.files.set(file.id, file.name);
        return { fileId: file.id, fileName: file.name, contentType: file.contentType };
      });
      publish(state, {
        ...identity, type: 'token', mode: 'live', senderKind: data.isHumanAgent ? 'human' : data.actor === 'bot' ? 'bot' : 'customer',
        text: data.text ?? '', attachments, providerMessageId: data.providerMessageId,
        ...(messageUpdated ? { messageUpdated: true } : {}),
      });
      if (data.isHumanAgent) {
        state.humanConnected = true;
        rememberPresentation(state, { senderKind: 'human', senderName: data.senderName });
      }
    } else if (event.type === 'error') {
      if (event.operation?.startsWith('read_receipts')) {
        publish(state, { ...identity, type: 'token', mode: 'live', senderKind: 'system',
          liveCategory: 'system', text: 'Read status is temporarily unavailable.' });
        return;
      }
      if (event.operation === 'handoff_context') {
        publish(state, { ...identity, type: 'token', mode: 'live', senderKind: 'system', liveCategory: 'system',
          text: 'Some prior context could not be confirmed with live support.' });
        return;
      }
      // The sending caller owns message feedback. Duplicating scan rejection on the
      // live subscription would be interpreted by older hosts as a failed session.
      if (event.operation === 'scan') return;
      if (state.recovery === 'starting') return; // The startup barrier reports its final outcome.
      publish(state, { ...identity, type: 'error', error: event.code ?? 'live_agent_unavailable' });
    } else if (event.type === 'state' && ['recovering', 'auth-failed'].includes(event.state)) {
      state.authRecovering = event.state === 'recovering';
      publishPresentation(state, {
        ...identity, type: 'token', mode: 'live', senderKind: 'system',
        liveCategory: event.state === 'recovering' ? 'connecting' : 'error',
        recoveryState: event.state,
        text: event.state === 'recovering' ? 'Reconnecting to live support…' : 'Live authentication could not be renewed. Please end this chat.',
      });
    } else if (event.type === 'state' && event.state === 'active' && state.authRecovering) {
      state.authRecovering = false;
      state.presentation = {
        phase: state.humanConnected ? 'connected' : 'waiting',
        senderName: state.humanConnected ? state.presentation?.senderName : undefined,
      };
      publish(state, {
        ...identity, type: 'token', mode: 'live', senderKind: 'system',
        liveCategory: 'system', recoveryState: 'active', text: 'Live support reconnected.',
      });
    } else if (event.type === 'postchat-survey') {
      state.postChatSurveyUrl = event.survey.url;
    } else if (event.type === 'state' && event.state === 'ended') {
      clearResume(state);
      const terminal = {
        ...identity, type: 'done', mode: 'ended', text: data.text, postChatSurveyUrl: state.postChatSurveyUrl,
      };
      if (state.ending || !state.completed) {
        state.terminal = terminal;
        return;
      }
      if (!state.ended) {
        state.ended = true;
        publish(state, terminal);
      }
    } else if (event.type === 'lifecycle' || event.type === 'typing') {
      const category = event.type === 'typing' ? 'typing' : lifecycleCategory(data.type);
      if (category === 'agent-joined') state.humanConnected = true;
      else if (['agent-left', 'transferred', 'ended'].includes(category)) state.humanConnected = false;
      const frame = {
        ...identity, type: 'token', mode: 'live', senderKind: 'system',
        liveCategory: category, text: data.text ?? '',
      };
      if (presentationPhase(frame)) publishPresentation(state, frame);
      else publish(state, frame);
    }
  }

  async function startProvider(id, state) {
    if (state.transportFailure) throw state.transportFailure;
    if (!state.started) {
      publishPresentation(state, {
        type: 'token', mode: 'live', senderKind: 'system', liveCategory: 'connecting',
        recoveryState: 'recovering', text: 'Connecting to live support…',
      });
      state.started = (async () => {
        await state.client.start();
        if (state.transportFailure) throw state.transportFailure;
        if (state.disposed || state.retiring) throw aborted();
        try {
          for (const message of await state.client.getHistory()) {
            normalizedEvent(id, state, { type: 'message', message });
          }
        } catch {
          publish(state, { type: 'error', error: 'live_history_unavailable' });
        }
        if (!state.humanConnected) {
          publishPresentation(state, {
            type: 'token', mode: 'live', senderKind: 'system', liveCategory: 'waiting',
            recoveryState: 'active', text: 'Waiting for a support agent…',
          });
        }
      })();
    }
    await state.started;
  }

  async function drainUnsent(id, state) {
    // Remove before attempting delivery: ambiguous failures must never auto-resend.
    for (const { text, clientOperationId, signal } of state.unsent.splice(0)) {
      try { await api.send(id, text, signal, clientOperationId); }
      catch (error) {
        if (signal?.aborted) continue;
        const operationId = clientOperationId ?? error.clientOperationId;
        if (error.operation === 'scan') {
          publish(state, {
            type: 'token', mode: 'live', senderKind: 'system', liveCategory: 'message-scan-failed', text: '',
            error: error.code, operation: 'scan', retryable: error.retryable,
            ...(error.status !== undefined ? { status: error.status } : {}),
            ...(operationId ? { clientOperationId: operationId } : {}),
          });
          continue;
        }
        publish(state, { type: 'error', error: error.code ?? 'live_agent_unavailable',
          ...(operationId ? { clientOperationId: operationId } : {}) });
      }
    }
  }

  async function start(id, closing = false) {
    const state = stateFor(id);
    if (state.transportFailure) throw state.transportFailure;
    if (!state.enabled || !state.bound) throw new Error('Browser live transport was not negotiated.');
    if (state.disposed || state.retiring || (state.ending && !closing)) throw aborted();
    if (!state.ready) {
      state.ready = (async () => {
        for (let attempt = 0; attempt < 3; attempt++) {
          if (state.disposed || state.retiring || (state.ending && !closing)) throw aborted();
          state.recovery = 'starting';
          const generation = ++state.attempt;
          try {
            const { createOmnichannelClient } = await (options.loadClient ?? loadClient)();
            if (state.transportFailure) throw state.transportFailure;
            if (state.disposed || state.retiring || (state.ending && !closing)) throw aborted();
            const client = createOmnichannelClient({
              bootstrap: async () => {
                const bootstrap = await bootstrapControl(id, state, generation);
                if (state.transportFailure) throw state.transportFailure;
                if (generation !== state.attempt || state.retiring || (state.ending && !closing)) throw aborted();
                state.requiresMessageScan = bootstrap.capabilities?.requiresMessageScan === true;
                return {
                  chatSessionId: bootstrap.chatSessionId, orgId: bootstrap.orgId, orgUrl: bootstrap.orgUrl,
                  widgetId: bootstrap.widgetId, customContext: bootstrap.customContext,
                  reconnectId: bootstrap.reconnectId, initialMessages: bootstrap.initialMessages,
                  capabilities: { requiresMessageScan: state.requiresMessageScan },
                  ...(bootstrap.restoreRequired ? {
                    omnichannelConversationId: bootstrap.omnichannelConversationId, restoreRequired: true,
                  } : {}),
                };
              },
              getAuthToken: async chatSessionId => (
                await request(id, state, 'token', { chatSessionId, context: await contextFor(id) })
              ).token,
              scanMessage: async ({ chatSessionId, message, signal }) => {
                try {
                  if (generation !== state.attempt || chatSessionId !== state.sessionId) throw aborted();
                  const verdict = await request(id, state, 'scan', { chatSessionId, message }, undefined, signal);
                  if (generation !== state.attempt || state.disposed || state.retiring || state.ending || state.ended
                    || state.lifetime.signal.aborted || signal.aborted || chatSessionId !== state.sessionId) throw aborted();
                  if (verdict?.allowed === true && verdict.code === undefined) {
                    return { allowed: true, ...(verdict.scanUnavailable === true ? { scanUnavailable: true } : {}) };
                  }
                  if (verdict?.allowed === false && verdict.code === 'live_chat_message_blocked') {
                    return { allowed: false, code: 'live_chat_message_blocked' };
                  }
                  throw scanFailure();
                } catch (error) { throw scanFailure(error); }
              },
              onSessionStarted: ids => rememberSession(state, ids),
              complete: async chatSessionId => {
                await completeControl(id, state, chatSessionId);
                if (state.terminal && !state.ending) {
                  state.ended = true;
                  publish(state, state.terminal);
                  state.terminal = null;
                }
              },
              onEvent: event => { if (generation === state.attempt) normalizedEvent(id, state, event); },
            });
            state.client = client;
            const initialized = await client.initialize();
            if (state.transportFailure) throw state.transportFailure;
            if (state.disposed || state.retiring || (state.ending && !closing)) throw aborted();
            state.survey = initialized.preChatSurvey ?? null;
            if (!state.survey) await startProvider(id, state);
            if (state.transportFailure) throw state.transportFailure;
            if (state.disposed || state.retiring) throw aborted();
            state.recovery = 'active';
            if (attempt > 0) publish(state, {
              type: 'token', mode: 'live', senderKind: 'system', liveCategory: 'system',
              recoveryState: 'active', text: 'Live support reconnected.',
            });
            return client;
          } catch (error) {
            state.failure = error;
            if (state.transportFailure) throw state.transportFailure;
            if (state.bootstrapRequest?.status === 'unknown'
              && !(state.bootstrapRequest.responseStatus >= 500 || state.bootstrapRequest.responseStatus === 429)) {
              error.startupRetryable = false;
            }
            if (state.ending && !state.started && error.name === 'AbortError') error.startupSafe = true;
            if (state.disposed || state.retiring || (state.ending && !closing)) throw aborted();
            if (error.startupRetryable !== true || attempt === 2) {
              state.recovery = 'failed';
              publishPresentation(state, {
                type: 'error', mode: 'live', senderKind: 'system', liveCategory: 'error',
                recoveryState: 'failed', error: error.code ?? 'live_agent_unavailable',
              });
              throw Object.assign(error, { recoveryState: 'failed', retryable: false });
            }
            await state.client.dispose();
            state.client = null;
            state.started = null;
            state.survey = null;
            state.recovery = 'recovering';
            publishPresentation(state, {
              type: 'token', mode: 'live', senderKind: 'system', liveCategory: 'connecting',
              recoveryState: 'recovering', text: 'Reconnecting to live support…',
            });
            await new Promise((resolve, reject) => {
              const signal = state.retryAbort.signal;
              const cancel = () => { clearTimeout(timer); reject(aborted()); };
              const timer = setTimeout(() => { signal.removeEventListener('abort', cancel); resolve(); }, 1000 * 2 ** attempt);
              if (signal.aborted) cancel();
              else signal.addEventListener('abort', cancel, { once: true });
            });
          }
        }
      })();
    }
    return state.ready;
  }

  const api = {
    enabled: id => sessions.get(id)?.enabled === true,
    isBound: id => sessions.get(id)?.bound === true,
    canSubmitPreChatSurvey: id => {
      const state = sessions.get(id);
      return !!state?.survey && !state.started && !state.disposed && !state.retiring && !state.ended;
    },
    isActive: id => {
      const state = sessions.get(id);
      return !!state?.bound && !state.ended && !state.disposed && !state.retiring;
    },
    isEnding: id => sessions.get(id)?.ending === true,
    getActiveConversationId: () => [...sessions].find(([, state]) =>
      state.bound && !state.ended && !state.disposed && !state.retiring)?.[0],
    hasBrowserSession: () => [...sessions.values()].some(state => state.bound),
    async initialize(id, response) {
      if (sessions.get(id)?.retiring) await api.dispose(id);
      if (sessions.get(id)?.disposed) sessions.delete(id);
      const state = stateFor(id);
      // Once browser-bound, NEVER fall through to the old server bridge, even
      // after a flag change or a failed start/send.
      state.enabled = state.bound || (!state.serverOwned && response.liveSdkEnabled === true);
      if (state.enabled) {
        const owner = await options.getIdentity?.(id);
        if (state.disposed || state.retiring) throw aborted();
        if (state.bound && state.owner !== undefined && owner !== state.owner) {
          await api.dispose(id);
          throw aborted();
        }
        state.owner = owner;
      }
      if (Array.isArray(response.history)) {
        state.history = response.history.filter(row => !row.isLiveAgent && !row.liveCategory && !row.messageId?.startsWith('sdk:'));
      }
      if (state.enabled && (response.mode === 'live' || response.mode === 'ended')) {
        state.bound = true;
        state.bindingId ??= response.sessionId ?? id;
        state.ended = response.mode === 'ended';
      }
      const result = state.enabled
        ? { ...response, liveCapabilities: { ...state.capabilities },
          ...(state.bound ? { sessionId: state.bindingId } : {}) }
        : response;
      if (!state.enabled || !state.bound) return result;
      if (state.ended) clearResume(state);
      const history = await api.transcript(id);
      const displayed = new Set(history.map(row => row.messageId).filter(Boolean));
      for (const messageId of displayed) state.seen.add(messageId);
      if (!state.watching) state.pending = state.pending.filter(event => !displayed.has(event.messageId)
        || ['read-receipt', 'message-sent'].includes(event.liveCategory));
      return { ...result, history, liveCapabilities: { ...state.capabilities } };
    },
    observe(id, event) {
      const state = stateFor(id);
      if (event.liveSdkEnabled === false) {
        if (state.bound || state.ready || state.started) {
          if (!state.transportFailure) {
            state.transportFailure = Object.assign(new Error('live_transport_conflict'), {
              code: 'live_transport_conflict', recoveryState: 'failed', retryable: false,
              liveSdkDisabledReason: event.liveSdkDisabledReason,
            });
            state.recovery = 'failed';
            state.unsent.length = 0;
            publishPresentation(state, {
              type: 'error', mode: 'live', senderKind: 'system', liveCategory: 'error',
              error: 'live_transport_conflict', recoveryState: 'failed',
            });
          }
          throw state.transportFailure;
        }
        state.serverOwned = true;
        state.enabled = false;
      }
      if (state.transportFailure && !(state.ended && event.mode === 'ended')) throw state.transportFailure;
      // Streaming hosts defer provisional live tokens/actions until the terminal
      // handoff decision. An enabled init is permission, not transport ownership.
      if (event.type && event.type !== 'done') return event;
      if (state.enabled && (event.mode === 'live' || event.channelAction === 'handoff_to_omnichannel')) {
        state.bound = true;
        state.bindingId ??= event.sessionId ?? id;
        return { ...event, mode: 'live', sessionId: state.bindingId, liveSdkEnabled: true };
      }
      return event;
    },
    async *events(id, signal) {
      const state = stateFor(id);
      if (signal?.aborted) return;
      if (state.watching) throw new Error('A live subscription is already attached.');
      const subscription = new AbortController();
      state.subscription = subscription;
      state.watching = true;
      const wake = () => state.wake?.();
      signal?.addEventListener('abort', wake, { once: true });
      subscription.signal.addEventListener('abort', wake, { once: true });
      try {
        if (state.ended && !state.client) {
          yield { type: 'done', mode: 'ended', sessionId: state.bindingId, postChatSurveyUrl: state.postChatSurveyUrl };
          return;
        }
        let startupFailure;
        try {
          await start(id);
        } catch (error) {
          startupFailure = error;
        }
        if (state.survey && !state.pending.length) {
          yield { type: 'done', mode: 'prechat-survey', sessionId: state.bindingId, preChatSurvey: state.survey };
          return;
        }
        let replayedPhase;
        while (!signal?.aborted && !subscription.signal.aborted && !state.disposed && !state.retiring) {
          if (state.pending.length) {
            const event = state.pending.shift();
            replayedPhase = presentationPhase(event) ?? replayedPhase;
            yield event;
            if (event.mode === 'ended' || event.mode === 'prechat-survey') return;
          } else if (startupFailure) {
            throw startupFailure;
          } else if (replayedPhase !== state.presentation?.phase) {
            const replay = presentationFrame(state);
            replayedPhase = state.presentation?.phase;
            if (replay) yield replay;
          } else if (state.ended) {
            yield { type: 'done', mode: 'ended', sessionId: state.bindingId, postChatSurveyUrl: state.postChatSurveyUrl };
            return;
          } else {
            await new Promise(resolve => { state.wake = resolve; });
            state.wake = null;
          }
        }
      } finally {
        signal?.removeEventListener('abort', wake);
        subscription.signal.removeEventListener('abort', wake);
        state.watching = false;
        state.subscription = null;
        state.wake = null;
      }
    },
    async send(id, message, signal, clientOperationId) {
      if (signal?.aborted) throw aborted();
      validateOperationId(clientOperationId);
      const state = stateFor(id);
      if (state.ended && message.trim() !== '/endchat') throw new Error('live_chat_ended');
      if (/^\/(?:csat|thumbup|thumbdown|thumbclear|reconnect|chatstatus|readmarkstatus)\b/i.test(message.trim())) {
        throw new Error('live_command_unavailable');
      }
      if (message.trim() === '/endchat') {
        await api.end(id);
        return {
          type: 'done', mode: 'ended', sessionId: state.bindingId, liveCommand: 'endchat', liveCommandOk: true,
          postChatSurveyUrl: state.postChatSurveyUrl,
        };
      }
      const client = await start(id);
      if (state.transportFailure) throw state.transportFailure;
      if (signal?.aborted || state.disposed || state.retiring) throw aborted();
      if (state.survey) {
        // A pending pre-chat survey is answered by sending its serialized answer object, so the only
        // valid payload here is a JSON object. A plain chat message can still reach send() when a host
        // routes text straight through send() (rather than forwardUnsent) while the survey is
        // outstanding — e.g. after a client/coordinator state desync re-enables the composer. Treat any
        // non-object payload as ordinary text: buffer it behind the survey (mirroring forwardUnsent) and
        // re-surface the survey, instead of throwing a raw JSON SyntaxError that surfaces to the user.
        let answers;
        try {
          const parsed = JSON.parse(message);
          if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) answers = parsed;
        } catch { /* Not a survey answer — fall through to buffering below. */ }
        if (answers === undefined) {
          state.unsent.push({ text: message, clientOperationId, ...(state.requiresMessageScan ? { signal } : {}) });
          return { type: 'done', mode: 'prechat-survey', sessionId: state.bindingId, preChatSurvey: state.survey };
        }
        await client.submitPreChatSurvey(answers);
        await startProvider(id, state);
        state.survey = null;
        await drainUnsent(id, state);
      } else {
        const confirmed = await (state.requiresMessageScan && signal
          ? client.sendMessage(message, clientOperationId, signal)
          : clientOperationId === undefined ? client.sendMessage(message) : client.sendMessage(message, clientOperationId));
        if (state.transportFailure) throw state.transportFailure;
        if (state.disposed || state.retiring || signal?.aborted) throw aborted();
        const acknowledgement = acknowledge(state, confirmed, clientOperationId ?? confirmed?.clientOperationId);
        if (!acknowledgement || (clientOperationId && confirmed?.clientOperationId !== clientOperationId)) {
          throw Object.assign(new Error('send_confirmation_pending'), {
            code: 'send_confirmation_pending', operation: 'send', clientOperationId, retryable: false,
          });
        }
        return { type: 'done', mode: 'live', sessionId: state.bindingId, ...acknowledgement };
      }
      return { type: 'done', mode: 'live', sessionId: state.bindingId };
    },
    async forwardUnsent(id, message, signal, clientOperationId) {
      validateOperationId(clientOperationId);
      await start(id);
      if (signal?.aborted) throw aborted();
      const state = stateFor(id);
      if (state.survey) state.unsent.push({ text: message, clientOperationId, ...(state.requiresMessageScan ? { signal } : {}) });
      else return api.send(id, message, signal, clientOperationId);
    },
    async submitPreChatSurvey(id, answers) {
      const state = stateFor(id);
      const client = await start(id);
      await client.submitPreChatSurvey(answers);
      if (state.transportFailure) throw state.transportFailure;
      await startProvider(id, state);
      state.survey = null;
      await drainUnsent(id, state);
    },
    async end(id) {
      const state = stateFor(id);
      if (state.ended && !state.client) return;
      if (state.endRequest) return state.endRequest;
      state.ending = true;
      state.retryAbort.abort();
      state.endRequest = (async () => {
        // A denied, never-dispatched startup owns no remote control/provider state.
        // Retire its local client without waiting for credentials or creating a binding.
        if (state.transportFailure && !state.sessionId && !state.started
          && !state.bootstrapUncertain && !state.bootstrapRequest?.dispatched) {
          await state.client?.dispose();
          state.client = null;
          state.ready = null;
          state.bootstrapRequest = null;
        } else {
          if (state.ready) await state.ready.catch(() => undefined);
          await settleBootstrap(state);
          if (!state.sessionId && !state.transportFailure) {
            const bootstrap = bootstrapControl(id, state);
            await settleBootstrap(state);
            await bootstrap;
          }
          if ((state.resumeHint || state.reconnectId) && state.recovery !== 'active' && (!state.client || state.failure?.startupSafe)) {
            if (state.transportFailure) throw new Error('restore_cleanup_required');
            // Explicit End may rejoin ONLY the saved provider to close it, never create a
            // replacement chat. Unknown start/close outcomes cannot enter this path.
            await state.client?.dispose();
            state.client = null;
            state.ready = null;
            state.started = null;
            state.retryAbort = new AbortController();
            await start(id, true);
          }
          // A dispatched bootstrap must settle first. Close only its confirmed binding;
          // unknown outcomes retain ownership and never trigger another bootstrap.
          if (state.client) await state.client.end();
        }
        if (!state.completed) {
          if (state.resumeHint || state.reconnectId) throw new Error('restore_cleanup_required');
          if (state.sessionId) await completeControl(id, state, state.sessionId);
          else {
            clearResume(state);
            state.completed = true;
          }
        }
        state.ended = true;
        state.unsent.length = 0;
        publish(state, state.terminal ?? {
          type: 'done', mode: 'ended', sessionId: state.bindingId, postChatSurveyUrl: state.postChatSurveyUrl,
        });
        state.terminal = null;
      })().finally(() => {
        state.ending = false;
        state.endRequest = null;
      });
      return state.endRequest;
    },
    async typing(id) {
      const client = await start(id);
      await client.sendTyping();
    },
    async markRead(id, messageId) {
      const state = sessions.get(id);
      const visible = () => typeof document !== 'undefined' && document.visibilityState === 'visible';
      // The conflict is already reported; presentation reads must not retry it.
      if (!state || state.transportFailure || state.disposed || state.retiring || state.ending || state.ended || !visible()) return;
      if (options.getIdentity && await options.getIdentity(id) !== state.owner) throw aborted();
      if (state.transportFailure || state.disposed || state.retiring || state.ending || state.ended || !visible()) return;
      const message = state.messages.get(messageId);
      if (!message?.isHumanAgent || !message.providerMessageId || state.read.has(messageId)) return;
      if (!state.reading.has(messageId)) {
        const reading = state.client.markRead(message.providerMessageId).then(() => {
          if (!state.disposed && !state.retiring) state.read.add(messageId);
        }).finally(() => { state.reading.delete(messageId); });
        state.reading.set(messageId, reading);
      }
      return state.reading.get(messageId);
    },
    async download(id, fileId) {
      const state = stateFor(id);
      const client = await start(id);
      return { blob: await client.downloadAttachment(fileId), fileName: state.files.get(fileId) ?? 'attachment' };
    },
    async transcript(id) {
      const state = stateFor(id);
      if (state.disposed || state.retiring) throw aborted();
      if (state.bound && !state.ended) {
        const alreadyStarted = !!state.started;
        const client = await start(id);
        if (alreadyStarted && !state.survey) {
          for (const message of await client.getHistory()) {
            if (!state.messages.has(sourceId({ ...message, chatSessionId: state.sessionId }))) {
              normalizedEvent(id, state, { type: 'message', message });
            }
          }
        }
      }
      const messages = [...state.messages.values()]
        .sort((left, right) => Date.parse(left.occurredAt) - Date.parse(right.occurredAt));
      return mergeOmnichannelHistory(state.history, messages);
    },
    rememberAiTurn(id, message, reply, caseDetails) {
      const state = stateFor(id);
      if (!state.enabled || state.disposed || state.retiring) return;
      const at = new Date().toISOString();
      if (message) state.history.push({ role: 'user', text: message, at });
      if (reply) state.history.push({ role: 'agent', text: reply, at, ...(caseDetails ? { caseDetails } : {}) });
    },
    detach(id) {
      for (const key of id ? [id] : sessions.keys()) sessions.get(key)?.subscription?.abort();
    },
    async dispose(id) {
      const ids = id ? [id] : [...sessions.keys()];
      const closing = [];
      for (const key of ids) {
        const state = sessions.get(key);
        if (!state || state.disposed) continue;
        if (state.closing) { closing.push(state.closing); continue; }
        if (!state.retiring) {
          state.teardownHeaders = state.lastHeaders ? Object.freeze({ ...state.lastHeaders }) : null;
          state.retiring = true;
        }
        state.retryAbort.abort();
        state.subscription?.abort();
        state.wake?.();
        state.closing = (async () => {
          // A bootstrapped prechat session owns backend control state even though
          // no provider start exists. Rejected readiness must not skip cleanup.
          if (state.ready) await state.ready.catch(() => undefined);
          await settleBootstrap(state);
          if (state.client && !state.completed) await state.client.end();
          if (state.sessionId && !state.completed) {
            if (state.resumeHint || state.reconnectId) throw new Error('restore_cleanup_required');
            await completeControl(key, state, state.sessionId);
          }
          await state.client?.dispose();
          clearResume(state);
          state.disposed = true;
          state.lifetime.abort();
          state.pending.length = 0;
          state.lastHeaders = null;
          state.teardownHeaders = null;
          state.bootstrapRequest = null;
          state.bootstrapUncertain = false;
          state.controlEndRequest = null;
          state.client = null;
          state.ready = null;
          state.started = null;
          state.files.clear();
          state.seen.clear();
          state.acknowledgedOperations.clear();
          state.messages.clear();
          state.history.length = 0;
          state.unsent.length = 0;
          state.survey = null;
          state.terminal = null;
          state.postChatSurveyUrl = undefined;
        })().finally(() => {
          // Keep the old client, binding and frozen auth until cleanup confirms.
          // A later explicit disposal may retry control completion, never a send.
          if (!state.disposed) state.closing = null;
        });
        closing.push(state.closing);
      }
      const results = await Promise.allSettled(closing);
      const failure = results.find(result => result.status === 'rejected');
      if (failure) throw failure.reason;
    },
  };
  coordinators.add(api);
  return api;
}

// All generic-widget hosts share this decorator. Their legacy implementation is
// called verbatim unless /init explicitly negotiated browser live transport.
export function withOmnichannelTransport(legacy, options) {
  const live = createOmnichannelCoordinator(options);
  let identity;
  let negotiated = false;
  const revisions = new Map();
  const negotiations = new Map();
  const awaitSignal = (promise, signal) => {
    if (!signal) return promise;
    if (signal.aborted) return Promise.reject(signal.reason ?? aborted());
    return new Promise((resolve, reject) => {
      const cancel = () => reject(signal.reason ?? aborted());
      signal.addEventListener('abort', cancel, { once: true });
      promise.then(value => {
        signal.removeEventListener('abort', cancel);
        resolve(value);
      }, error => {
        signal.removeEventListener('abort', cancel);
        reject(error);
      });
    });
  };
  const invalidate = id => {
    for (const key of id ? [id] : revisions.keys()) {
      revisions.set(key, (revisions.get(key) ?? 0) + 1);
      const entry = negotiations.get(key);
      if (entry) {
        entry.controller.abort(aborted());
        entry.owner = undefined;
        // Invalidation must also release any successful init's retained display history.
        entry.promise = Promise.reject(aborted());
        entry.promise.catch(() => {});
      }
    }
  };
  const awaitNegotiation = async (id, signal, allowBoundFailure = false) => {
    if (signal?.aborted) throw aborted();
    const entry = negotiations.get(id);
    if (!entry) return;
    try {
      await awaitSignal(entry.promise, signal);
    } catch (error) {
      // A rejected startup must still permit guarded cleanup of its existing binding.
      // Every other operation retains the rejection barrier, including legacy fallback.
      if (!allowBoundFailure || !live.isBound(id)) throw error;
    }
    if (signal?.aborted || revisions.get(id) !== entry.revision) throw aborted();
    if (options.getIdentity) {
      const owner = await options.getIdentity(id);
      if (signal?.aborted || revisions.get(id) !== entry.revision) throw aborted();
      if (owner !== entry.owner) {
        invalidate(id);
        await live.dispose(id);
        throw aborted();
      }
    }
    if (signal?.aborted || revisions.get(id) !== entry.revision) throw aborted();
  };
  const wrapChunks = async function* (chunks, id, message, signal, clientOperationId) {
    let forwarded = false;
    let acknowledgement;
    let remembered = false;
    let reply = '';
    let caseDetails;
    let channelAction;
    const pendingHandoff = [];
    for await (const chunk of chunks) {
      if (chunk.type === 'text') {
        reply += chunk.data ?? '';
        if (chunk.caseDetails) caseDetails = chunk.caseDetails;
      }
      if (chunk.type === 'done' && !remembered && chunk.meta?.retryMessage !== true) {
        remembered = true;
        live.rememberAiTurn(id, message, reply || chunk.data, caseDetails);
      }
      if (chunk.type === 'action') channelAction = chunk.data;
      if (live.enabled(id) && !live.isBound(id) && (pendingHandoff.length
        || chunk.type === 'live' || (chunk.type === 'action' && chunk.data === 'handoff_to_omnichannel'))
        && chunk.type !== 'done' && chunk.type !== 'error') {
        pendingHandoff.push(chunk);
        continue;
      }
      if (chunk.type === 'done') live.observe(id, {
        ...chunk.meta, type: 'done', channelAction,
        mode: chunk.meta?.mode ?? (chunk.meta?.liveSession ? 'live' : undefined),
      });
      if (chunk.type === 'done') yield* pendingHandoff.splice(0);
      if (chunk.type === 'done' && live.isBound(id)) {
        if (chunk.meta?.retryMessage === true && message?.trim() && !forwarded) {
          forwarded = true;
          try {
            const sent = await live.forwardUnsent(id, message, signal, clientOperationId);
            if (sent?.messageId) acknowledgement = {
              liveCategory: 'message-sent', messageId: sent.messageId,
              ...(sent.clientOperationId ? { clientOperationId: sent.clientOperationId } : {}),
              readSequence: sent.readSequence, readOn: sent.readOn,
            };
          } catch (error) {
            if (signal?.aborted) throw error;
            if (error.operation === 'scan') {
              if (!live.isActive(id) || live.isEnding(id)) throw error;
              // Handoff succeeded even though its first text was not sent. Preserve
              // the completion so the host starts receiving this existing session.
              yield { type: 'live', liveKind: 'system', liveCategory: 'message-scan-failed', data: '',
                code: error.code, error: error.code,
                ...(error.clientOperationId ? { clientOperationId: error.clientOperationId } : {}) };
              if (signal?.aborted || !live.isActive(id) || live.isEnding(id)) throw aborted();
            } else {
              // Keep observing the established channel, but never claim delivery or resend.
              yield { type: 'error', data: '', meta: { liveSession: true },
                code: error.code, error: error.code,
                ...(error.clientOperationId ? { clientOperationId: error.clientOperationId } : {}),
                ...(error.recoveryState ? { recoveryState: error.recoveryState } : {}) };
              return;
            }
          }
        }
        const { retryMessage: _consumed, ...meta } = chunk.meta ?? {};
        yield { ...chunk, ...acknowledgement, meta: { ...meta, liveSession: true } };
      } else yield chunk;
    }
  };
  const transport = {
    ...legacy,
    getActiveConversationId: () => live.getActiveConversationId(),
    hasBrowserSession: () => live.hasBrowserSession(),
    detach: id => {
      invalidate(id);
      live.detach(id);
    },
    init(id) {
      const pending = negotiations.get(id);
      if (pending?.pending && revisions.get(id) === pending.revision) return pending.promise;
      const revision = (revisions.get(id) ?? 0) + 1;
      revisions.set(id, revision);
      const entry = { revision, controller: new AbortController(), pending: true, owner: undefined, promise: null };
      negotiations.set(id, entry);
      const checkCurrent = () => {
        if (entry.controller.signal.aborted || revisions.get(id) !== revision) throw aborted();
      };
      const operation = Promise.resolve().then(async () => {
        entry.owner = await options.getIdentity?.(id);
        checkCurrent();
        const previous = live.getActiveConversationId();
        if (previous && previous !== id) {
          invalidate(previous);
          await live.dispose(previous);
        }
        checkCurrent();
        const initialized = await legacy.init(id);
        checkCurrent();
        if (options.getIdentity && await options.getIdentity(id) !== entry.owner) throw aborted();
        checkCurrent();
        const response = await live.initialize(id, initialized);
        checkCurrent();
        negotiated = live.enabled(id);
        if (negotiated) identity = entry.owner;
        return response;
      });
      const timeout = setTimeout(() => entry.controller.abort(new Error('live_initialization_timeout')), 30_000);
      entry.promise = awaitSignal(operation, entry.controller.signal).finally(() => {
        clearTimeout(timeout);
        entry.pending = false;
      });
      // Retain a rejected negotiation until explicit init retries; queued sends must not fall through.
      entry.promise.catch(() => {});
      return entry.promise;
    },
    async *stream(message, id, signal, clientOperationId) {
      await awaitNegotiation(id, signal);
      if (live.isBound(id)) {
        const event = await live.send(id, message, signal, clientOperationId);
        yield {
          type: 'done', data: '', meta: { liveSession: event.mode === 'live' },
          ...(event.messageId ? {
            liveCategory: 'message-sent', messageId: event.messageId,
            ...(event.clientOperationId ? { clientOperationId: event.clientOperationId } : {}),
            readSequence: event.readSequence, readOn: event.readOn,
          } : {}),
        };
        return;
      }
      yield* wrapChunks(legacy.stream(message, id, signal), id, message, signal, clientOperationId);
    },
    ...(legacy.submitPrompt ? {
      async *submitPrompt(prompt, values, id, signal) {
        await awaitNegotiation(id, signal);
        if (live.isBound(id)) throw new Error('A live conversation cannot submit an AI prompt.');
        yield* wrapChunks(legacy.submitPrompt(prompt, values, id, signal), id, undefined, signal);
      },
    } : {}),
    async *startLive(id, signal) {
      await awaitNegotiation(id, signal);
      if (!live.isBound(id)) { yield* legacy.startLive(id, signal); return; }
      for await (const event of live.events(id, signal)) {
        if (event.liveCategory === 'message-sent' && !event.messageId) continue;
        yield liveFrameFor(event);
      }
    },
    async sendLive(message, id, signal, clientOperationId) {
      await awaitNegotiation(id, signal);
      if (!live.isBound(id)) return legacy.sendLive(message, id, signal);
      const event = await live.send(id, message, signal, clientOperationId);
      return event.mode === 'ended' || event.mode === 'prechat-survey' ? [liveFrameFor(event)] : event.messageId
        ? [liveFrameFor({ ...event, liveCategory: 'message-sent' })] : [];
    },
    async endLive(id) {
      await awaitNegotiation(id, undefined, true);
      if (live.isBound(id)) await live.end(id);
      else await legacy.endLive?.(id);
    },
    async markRead(id, messageId) {
      await awaitNegotiation(id);
      if (live.isBound(id)) await live.markRead(id, messageId);
      else await legacy.markRead?.(id, messageId);
    },
    async *submitPreChatSurvey(answers, id, signal) {
      await awaitNegotiation(id, signal);
      if (!live.isBound(id)) { yield* legacy.submitPreChatSurvey(answers, id, signal); return; }
      if (signal?.aborted) throw aborted();
      await live.submitPreChatSurvey(id, answers);
    },
    get fetchTranscript() {
      if (!legacy.fetchTranscript && !negotiated) return undefined;
      return async (id, signal) => {
        await awaitNegotiation(id, signal);
        if (!live.isBound(id)) return legacy.fetchTranscript?.(id, signal) ?? null;
        if (signal?.aborted) throw aborted();
        const entries = await live.transcript(id);
        if (signal?.aborted) throw aborted();
        return { conversationId: id, entries };
      };
    },
    async refreshIdentity() {
      const current = await options.getIdentity?.();
      for (const [id, entry] of negotiations) {
        if (entry.pending && (!options.getIdentity || entry.owner !== current)) invalidate(id);
      }
      const latest = [...negotiations].at(-1);
      if (!latest || !live.isBound(latest[0])) {
        if (latest && options.getIdentity && latest[1].owner !== current) {
          await transport.init(latest[0]);
        } else await legacy.refreshIdentity?.();
        return;
      }
      if (options.getIdentity && current !== identity) {
        identity = current;
        invalidate();
        await live.dispose();
        return;
      }
      await legacy.refreshIdentity?.();
    },
    dispose: id => {
      invalidate(id);
      return live.dispose(id);
    },
  };
  return transport;
}
